import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import DailyBriefingBar from '../components/DailyBriefingBar';
import {
  ArrowLeft, Plus, Calendar, Filter, ChevronDown, X, Clock,
  Instagram, Globe, Mail, Video, FileText, Edit2, Trash2, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { id: 'instagram', label: 'Instagram', color: '#E1306C' },
  { id: 'tiktok', label: 'TikTok', color: '#000000' },
  { id: 'linkedin', label: 'LinkedIn', color: '#0A66C2' },
  { id: 'youtube', label: 'YouTube', color: '#FF0000' },
  { id: 'twitter', label: 'X / Twitter', color: '#1DA1F2' },
  { id: 'facebook', label: 'Facebook', color: '#1877F2' },
  { id: 'website', label: 'Website / Blog', color: '#D4AF37' },
  { id: 'email', label: 'Email / Newsletter', color: '#8B7355' },
  { id: 'other', label: 'Other', color: '#6B7280' },
];

const CONTENT_TYPES = [
  { id: 'post', label: 'Post' },
  { id: 'story', label: 'Story' },
  { id: 'reel', label: 'Reel / Short' },
  { id: 'blog', label: 'Blog Article' },
  { id: 'video', label: 'Video' },
  { id: 'newsletter', label: 'Newsletter' },
  { id: 'campaign', label: 'Campaign' },
  { id: 'other', label: 'Other' },
];

const STATUSES = [
  { id: 'draft', label: 'Draft', color: '#8B7355', bg: '#F5F1EB' },
  { id: 'scheduled', label: 'Scheduled', color: '#1E3A8A', bg: '#DBEAFE' },
  { id: 'published', label: 'Published', color: '#166534', bg: '#DCFCE7' },
];

const PLATFORM_MAP = Object.fromEntries(PLATFORMS.map(p => [p.id, p]));
const CONTENT_TYPE_MAP = Object.fromEntries(CONTENT_TYPES.map(c => [c.id, c]));
const STATUS_MAP = Object.fromEntries(STATUSES.map(s => [s.id, s]));

const ContentCalendar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [form, setForm] = useState({
    company_id: '', title: '', description: '', platform: 'instagram',
    content_type: 'post', scheduled_date: '', assigned_to: '', status: 'draft'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [itemsRes, companiesRes] = await Promise.all([
        axios.get(`${API_URL}/api/content-calendar`, { withCredentials: true }),
        axios.get(`${API_URL}/api/companies`, { withCredentials: true })
      ]);
      setItems(itemsRes.data);
      setCompanies(companiesRes.data.filter(c => c.status !== 'dissolving'));
      if (!form.company_id && companiesRes.data.length > 0) {
        setForm(prev => ({ ...prev, company_id: companiesRes.data[0].id }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      company_id: companies[0]?.id || '', title: '', description: '', platform: 'instagram',
      content_type: 'post', scheduled_date: '', assigned_to: '', status: 'draft'
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/content-calendar/${editingId}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/content-calendar`, form, { withCredentials: true });
      }
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving content item:', error);
    }
  };

  const handleEdit = (item) => {
    setForm({
      company_id: item.company_id, title: item.title, description: item.description || '',
      platform: item.platform, content_type: item.content_type,
      scheduled_date: item.scheduled_date || '', assigned_to: item.assigned_to || '',
      status: item.status
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this content item?')) return;
    try {
      await axios.delete(`${API_URL}/api/content-calendar/${id}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/content-calendar/${id}`, { status: newStatus }, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const companyMap = Object.fromEntries(companies.map(c => [c.id, c]));
  const allCompanies = [...companies, ...([{ id: 'all', name: 'All Companies' }])];

  const filtered = items.filter(item => {
    if (filterCompany !== 'all' && item.company_id !== filterCompany) return false;
    if (filterStatus !== 'all' && item.status !== filterStatus) return false;
    if (filterPlatform !== 'all' && item.platform !== filterPlatform) return false;
    return true;
  });

  // Group by date for calendar view
  const groupedByDate = {};
  filtered.forEach(item => {
    const date = item.scheduled_date || 'unscheduled';
    if (!groupedByDate[date]) groupedByDate[date] = [];
    groupedByDate[date].push(item);
  });

  const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
    if (a === 'unscheduled') return 1;
    if (b === 'unscheduled') return -1;
    return a.localeCompare(b);
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F7' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#D4AF37' }}></div>
          <p className="mt-4" style={{ color: '#8B7355' }}>Loading content calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F7' }} data-testid="content-calendar-page">
      <DailyBriefingBar />

      <header className="bg-white" style={{ borderBottom: '1px solid #E5E1DB' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 mb-4 transition-all duration-200" style={{ color: '#8B7355' }} data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" /><span>Back to Dashboard</span>
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: '#D4AF37' + '20' }}>
                <Calendar className="w-7 h-7" style={{ color: '#D4AF37' }} />
              </div>
              <div>
                <h1 className="text-4xl font-light tracking-tight" style={{ color: '#2C2416' }}>Content Calendar</h1>
                <p className="mt-1" style={{ color: '#8B7355' }}>Plan, schedule & track content across all companies</p>
              </div>
            </div>
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="flex items-center gap-2 px-6 py-3 rounded-sm transition-all duration-200 hover:shadow-md"
              style={{ backgroundColor: '#D4AF37', color: 'white' }}
              data-testid="add-content-button"
            >
              <Plus className="w-5 h-5" /><span>Add Content</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="calendar-filters">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4" style={{ color: '#8B7355' }} />
            <span className="text-xs uppercase tracking-widest" style={{ color: '#8B7355' }}>Filter:</span>
          </div>
          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
            className="px-3 py-2 border rounded-sm text-sm focus:outline-none"
            style={{ borderColor: '#E5E1DB', color: '#2C2416' }}
            data-testid="filter-company"
          >
            <option value="all">All Companies</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border rounded-sm text-sm focus:outline-none"
            style={{ borderColor: '#E5E1DB', color: '#2C2416' }}
            data-testid="filter-status"
          >
            <option value="all">All Statuses</option>
            {STATUSES.map(s => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
          <select
            value={filterPlatform}
            onChange={(e) => setFilterPlatform(e.target.value)}
            className="px-3 py-2 border rounded-sm text-sm focus:outline-none"
            style={{ borderColor: '#E5E1DB', color: '#2C2416' }}
            data-testid="filter-platform"
          >
            <option value="all">All Platforms</option>
            {PLATFORMS.map(p => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
          <div className="ml-auto flex gap-1">
            <button
              onClick={() => setViewMode('list')}
              className="px-3 py-2 text-xs uppercase tracking-wider rounded-sm border transition-all"
              style={{ borderColor: viewMode === 'list' ? '#D4AF37' : '#E5E1DB', backgroundColor: viewMode === 'list' ? '#D4AF37' + '15' : 'white', color: viewMode === 'list' ? '#D4AF37' : '#8B7355' }}
              data-testid="view-list"
            >
              List
            </button>
            <button
              onClick={() => setViewMode('schedule')}
              className="px-3 py-2 text-xs uppercase tracking-wider rounded-sm border transition-all"
              style={{ borderColor: viewMode === 'schedule' ? '#D4AF37' : '#E5E1DB', backgroundColor: viewMode === 'schedule' ? '#D4AF37' + '15' : 'white', color: viewMode === 'schedule' ? '#D4AF37' : '#8B7355' }}
              data-testid="view-schedule"
            >
              Schedule
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {STATUSES.map(s => {
            const count = items.filter(i => i.status === s.id).length;
            return (
              <div key={s.id} className="text-center p-4 bg-white rounded-lg border" style={{ borderColor: '#E5E1DB' }} data-testid={`stat-${s.id}`}>
                <div className="text-3xl font-light mb-1" style={{ color: s.color }}>{count}</div>
                <div className="text-xs uppercase tracking-widest" style={{ color: '#8B7355' }}>{s.label}</div>
              </div>
            );
          })}
          <div className="text-center p-4 bg-white rounded-lg border" style={{ borderColor: '#E5E1DB' }} data-testid="stat-total">
            <div className="text-3xl font-light mb-1" style={{ color: '#D4AF37' }}>{items.length}</div>
            <div className="text-xs uppercase tracking-widest" style={{ color: '#8B7355' }}>Total</div>
          </div>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-white border rounded-sm shadow-sm p-8 mb-6" style={{ borderColor: '#E5E1DB' }} data-testid="content-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-light" style={{ color: '#2C2416' }}>
                {editingId ? 'Edit Content' : 'Schedule New Content'}
              </h2>
              <button onClick={resetForm} className="p-1"><X className="w-5 h-5" style={{ color: '#8B7355' }} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#2C2416' }}>Company</label>
                  <select value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} className="w-full px-4 py-3 border rounded-sm focus:outline-none" style={{ borderColor: '#E5E1DB' }} required data-testid="form-company">
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#2C2416' }}>Title</label>
                  <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-4 py-3 border rounded-sm focus:outline-none" style={{ borderColor: '#E5E1DB' }} placeholder="e.g. Product launch post" required data-testid="form-title" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#2C2416' }}>Platform</label>
                  <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="w-full px-4 py-3 border rounded-sm focus:outline-none" style={{ borderColor: '#E5E1DB' }} data-testid="form-platform">
                    {PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#2C2416' }}>Content Type</label>
                  <select value={form.content_type} onChange={(e) => setForm({ ...form, content_type: e.target.value })} className="w-full px-4 py-3 border rounded-sm focus:outline-none" style={{ borderColor: '#E5E1DB' }} data-testid="form-content-type">
                    {CONTENT_TYPES.map(ct => <option key={ct.id} value={ct.id}>{ct.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#2C2416' }}>Scheduled Date</label>
                  <input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="w-full px-4 py-3 border rounded-sm focus:outline-none" style={{ borderColor: '#E5E1DB' }} data-testid="form-date" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#2C2416' }}>Assigned To</label>
                  <input type="text" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="w-full px-4 py-3 border rounded-sm focus:outline-none" style={{ borderColor: '#E5E1DB' }} placeholder="e.g. Intern name, Staff member" data-testid="form-assigned" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#2C2416' }}>Status</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-4 py-3 border rounded-sm focus:outline-none" style={{ borderColor: '#E5E1DB' }} data-testid="form-status">
                    {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#2C2416' }}>Description / Brief</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-4 py-3 border rounded-sm focus:outline-none" style={{ borderColor: '#E5E1DB' }} placeholder="Content brief, copy, notes..." data-testid="form-description" />
              </div>
              <div className="flex gap-3">
                <button type="submit" className="px-6 py-3 rounded-sm transition-all duration-200 hover:shadow-md" style={{ backgroundColor: '#D4AF37', color: 'white' }} data-testid="form-submit">
                  {editingId ? 'Update' : 'Schedule'}
                </button>
                <button type="button" onClick={resetForm} className="px-6 py-3 bg-white border rounded-sm" style={{ borderColor: '#E5E1DB' }} data-testid="form-cancel">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Content List/Schedule */}
        {filtered.length === 0 ? (
          <div className="bg-white border rounded-sm shadow-sm p-12 text-center" style={{ borderColor: '#E5E1DB' }} data-testid="empty-calendar">
            <Calendar className="w-16 h-16 mx-auto mb-4" style={{ color: '#E5E1DB' }} />
            <h3 className="text-xl font-light mb-2" style={{ color: '#2C2416' }}>No content scheduled yet</h3>
            <p className="mb-4" style={{ color: '#8B7355' }}>Start planning your content by clicking "Add Content" above.</p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="space-y-3" data-testid="content-list">
            {filtered.map((item) => {
              const company = companyMap[item.company_id];
              const platform = PLATFORM_MAP[item.platform] || PLATFORM_MAP.other;
              const contentType = CONTENT_TYPE_MAP[item.content_type] || CONTENT_TYPE_MAP.other;
              const status = STATUS_MAP[item.status] || STATUS_MAP.draft;
              const isOverdue = item.scheduled_date && item.status !== 'published' && new Date(item.scheduled_date) < new Date();
              return (
                <div
                  key={item.id}
                  className="bg-white border rounded-sm p-5 transition-all duration-200 hover:shadow-md"
                  style={{ borderColor: '#E5E1DB', borderLeftWidth: '4px', borderLeftColor: platform.color }}
                  data-testid={`content-item-${item.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="font-medium text-lg" style={{ color: '#2C2416' }}>{item.title}</h3>
                        <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: status.bg, color: status.color }}>{status.label}</span>
                        <span className="text-xs px-2.5 py-1 rounded-full" style={{ backgroundColor: platform.color + '15', color: platform.color }}>{platform.label}</span>
                        <span className="text-xs px-2 py-1 rounded-full border" style={{ borderColor: '#E5E1DB', color: '#8B7355' }}>{contentType.label}</span>
                        {isOverdue && (
                          <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: '#FEF2F2', color: '#991B1B' }}>Overdue</span>
                        )}
                      </div>
                      {item.description && (
                        <p className="text-sm mb-2" style={{ color: '#8B7355' }}>{item.description}</p>
                      )}
                      <div className="flex items-center gap-4 text-xs flex-wrap" style={{ color: '#8B7355' }}>
                        {company && (
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: company.color }} />
                            {company.name}
                          </span>
                        )}
                        {item.scheduled_date && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(item.scheduled_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                          </span>
                        )}
                        {item.assigned_to && (
                          <span>Assigned: <strong>{item.assigned_to}</strong></span>
                        )}
                        <span>Added by {item.created_by_name || 'Unknown'} on {new Date(item.created_at).toLocaleDateString('en-GB')}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {item.status !== 'published' && (
                        <select
                          value={item.status}
                          onChange={(e) => handleStatusChange(item.id, e.target.value)}
                          className="text-xs px-2 py-1.5 border rounded-sm focus:outline-none"
                          style={{ borderColor: '#E5E1DB' }}
                          data-testid={`status-select-${item.id}`}
                        >
                          {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                        </select>
                      )}
                      <button onClick={() => handleEdit(item)} className="p-2 rounded-sm transition-all hover:bg-gray-50" data-testid={`edit-${item.id}`}>
                        <Edit2 className="w-4 h-4" style={{ color: '#8B7355' }} />
                      </button>
                      <button onClick={() => handleDelete(item.id)} className="p-2 rounded-sm transition-all hover:bg-red-50" data-testid={`delete-${item.id}`}>
                        <Trash2 className="w-4 h-4" style={{ color: '#DC2626' }} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Schedule View - grouped by date */
          <div className="space-y-6" data-testid="content-schedule">
            {sortedDates.map(date => (
              <div key={date} data-testid={`schedule-date-${date}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: date === 'unscheduled' ? '#F5F1EB' : '#D4AF37' + '20' }}>
                    <Calendar className="w-5 h-5" style={{ color: date === 'unscheduled' ? '#8B7355' : '#D4AF37' }} />
                  </div>
                  <h3 className="text-lg font-medium" style={{ color: '#2C2416' }}>
                    {date === 'unscheduled' ? 'Unscheduled' : new Date(date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </h3>
                  <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#F5F1EB', color: '#8B7355' }}>
                    {groupedByDate[date].length} item{groupedByDate[date].length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="space-y-2 ml-5 pl-8" style={{ borderLeft: '2px solid #E5E1DB' }}>
                  {groupedByDate[date].map(item => {
                    const platform = PLATFORM_MAP[item.platform] || PLATFORM_MAP.other;
                    const status = STATUS_MAP[item.status] || STATUS_MAP.draft;
                    const company = companyMap[item.company_id];
                    return (
                      <div key={item.id} className="bg-white border rounded-sm p-4 flex items-center gap-4 transition-all hover:shadow-sm" style={{ borderColor: '#E5E1DB' }}>
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: platform.color }} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm" style={{ color: '#2C2416' }}>{item.title}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: status.bg, color: status.color }}>{status.label}</span>
                            <span className="text-xs" style={{ color: platform.color }}>{platform.label}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: '#8B7355' }}>
                            {company && <span>{company.name}</span>}
                            {item.assigned_to && <span>Assigned: {item.assigned_to}</span>}
                            <span>by {item.created_by_name || 'Unknown'}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button onClick={() => handleEdit(item)} className="p-1.5 rounded-sm transition-all hover:bg-gray-50">
                            <Edit2 className="w-3.5 h-3.5" style={{ color: '#8B7355' }} />
                          </button>
                          <button onClick={() => handleDelete(item.id)} className="p-1.5 rounded-sm transition-all hover:bg-red-50">
                            <Trash2 className="w-3.5 h-3.5" style={{ color: '#DC2626' }} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ContentCalendar;
